import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react"
import {
	CURRENCIES,
	convertCurrencyAmount,
	getDefaultCurrency,
	normalizeCurrencyCode,
	type CurrencyCode,
	updateRates,
} from "@/lib/currency-data.ts"
import { CurrencyContext, type FormatOptions, type LocalFormatOptions } from "./currency-context.ts"

export function CurrencyProvider({ children }: { children: ReactNode }) {
	// Initialize with detected location or fallback to USD
	const [currencyCode, setCurrencyCode] = useState<CurrencyCode>(() => {
		const saved = localStorage.getItem("beediv-currency") as CurrencyCode
		return saved && CURRENCIES[saved] ? saved : getDefaultCurrency()
	})
	const [ratesVersion, setRatesVersion] = useState(0)

	const currency = CURRENCIES[currencyCode]

	// Persist selection
	useEffect(() => {
		localStorage.setItem("beediv-currency", currencyCode)
	}, [currencyCode])

	useEffect(() => {
		let cancelled = false
		void updateRates().then((updated) => {
			if (!cancelled && updated) {
				setRatesVersion((version) => version + 1)
			}
		})
		return () => {
			cancelled = true
		}
	}, [])

	// Optimize: Memoize helper functions
	const convert = useCallback(
		(amount: number, fromCurrency?: string) => {
			return convertCurrencyAmount(amount, currency.code, fromCurrency)
		},
		[currency.code, ratesVersion],
	)

	const format = useCallback(
		(amount: number, options?: FormatOptions) => {
			const { compact = false, skipConversion = false, fromCurrency, noDecimals = false } = options || {}

			// Flexible: Allow formatting raw local values (skipConversion) or convert USD -> Local
			const valueToFormat = skipConversion ? amount : convert(amount, fromCurrency)

			// A non-finite value means a currency conversion failed (missing FX rate).
			// Never render "NaN" — show a neutral placeholder instead.
			if (!Number.isFinite(valueToFormat)) return "—"

			return new Intl.NumberFormat(currency.locale, {
				style: "currency",
				currency: currency.code,
				notation: compact ? "compact" : "standard",
				compactDisplay: "short",
				minimumFractionDigits: compact || noDecimals ? 0 : 2,
				maximumFractionDigits: noDecimals ? 0 : 2,
			}).format(valueToFormat)
		},
		[currency, convert],
	)

	// Format an amount in its native/local currency without converting to base currency.
	// Use for per-position display (price, value, return, income) where the stock's
	// local currency is more meaningful than a converted base-currency figure.
	const localFormat = useCallback(
		(amount: number, localCurrency?: string, options?: LocalFormatOptions) => {
			const { compact = false, noDecimals = false } = options || {}
			const code = normalizeCurrencyCode(localCurrency) || currency.code
			try {
				return new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: code,
					notation: compact ? "compact" : "standard",
					compactDisplay: "short",
					minimumFractionDigits: compact || noDecimals ? 0 : 2,
					maximumFractionDigits: noDecimals ? 0 : 2,
				}).format(amount)
			} catch {
				// Unknown/invalid currency code — fall back to base currency display
				return format(amount, { skipConversion: true })
			}
		},
		[currency.code, format],
	)

	// Optimize: Memoize the context value object
	const value = useMemo(
		() => ({
			currency,
			setCurrency: setCurrencyCode,
			convert,
			format,
			localFormat,
		}),
		[currency, convert, format, localFormat],
	)

	return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
}
